/**
 * Yönetici Sınav Listesi — Spec Bölüm 7.1
 *
 *   GET /api/manager/exams → tüm sınavlar (en yeni → eski)
 *
 * Her satırda:
 *   - Erişim kodu + kopyala
 *   - Katılım sayısı
 *   - Mod (Anonim / Normal)
 *   - Geçerlilik tarihi
 *   - "Detay" linki
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Button, Card } from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';

interface ExamRow {
  id: string;
  accessCode: string;
  durationMin: number;
  questionCount: number;
  anonymousMode: boolean;
  isActive: boolean;
  validUntil: string;
  createdAt: string;
  _count: { attempts: number };
}

export function ExamsListPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending: sessionPending } = useSession();
  const [exams, setExams] = useState<ExamRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionPending) return;
    if (!session?.user) {
      setLocation('/yonetici/giris');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<{ exams: ExamRow[] }>('/api/manager/exams');
        if (!cancelled) setExams(data.exams);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message);
        else setError('Sınavlar yüklenemedi');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, sessionPending, setLocation]);

  if (sessionPending || (!exams && !error)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-5xl px-6 py-12">
          <p className="text-slate-500">Yükleniyor…</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-5xl px-6 py-12">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <button
              type="button"
              onClick={() => setLocation('/yonetici/dashboard')}
              className="text-sm font-medium text-blue-900 hover:underline"
            >
              ← Panel
            </button>
            <h1 className="mt-2 text-3xl font-bold text-slate-900">Sınavlarım</h1>
          </div>
          <Button onClick={() => setLocation('/yonetici/sinavlar/yeni')}>
            + Yeni Sınav
          </Button>
        </div>

        {error && (
          <div className="mb-6">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}

        {exams && exams.length === 0 && (
          <Card>
            <p className="text-center text-slate-600">
              Henüz sınav oluşturmadınız.
            </p>
            <div className="mt-4 flex justify-center">
              <Button onClick={() => setLocation('/yonetici/sinavlar/yeni')}>
                İlk Sınavı Oluştur
              </Button>
            </div>
          </Card>
        )}

        {exams && exams.length > 0 && (
          <div className="space-y-3">
            {exams.map((e) => {
              const validUntil = new Date(e.validUntil);
              const expired = validUntil < new Date();
              return (
                <button
                  type="button"
                  key={e.id}
                  onClick={() => setLocation(`/yonetici/sinavlar/${e.id}`)}
                  className="w-full rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <code className="font-mono text-lg font-bold text-blue-900">
                        {e.accessCode}
                      </code>
                      <div className="mt-1 flex flex-wrap gap-2 text-xs">
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                          {e.questionCount} soru · {e.durationMin} dk
                        </span>
                        {e.anonymousMode && (
                          <span className="rounded-full bg-purple-50 px-2 py-0.5 text-purple-900">
                            Anonim
                          </span>
                        )}
                        {expired ? (
                          <span className="rounded-full bg-red-50 px-2 py-0.5 text-red-700">
                            Süresi Doldu
                          </span>
                        ) : (
                          <span className="rounded-full bg-green-50 px-2 py-0.5 text-green-700">
                            Aktif
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-slate-900">
                        {e._count.attempts}
                      </p>
                      <p className="text-xs uppercase tracking-wider text-slate-500">
                        Katılım
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex justify-between text-xs text-slate-500">
                    <span>
                      Oluşturuldu:{' '}
                      {new Date(e.createdAt).toLocaleDateString('tr-TR')}
                    </span>
                    <span>
                      Geçerli:{' '}
                      {validUntil.toLocaleDateString('tr-TR')}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
